/**
 * web/src/screens/Notebook/hooks/share.ts — the share/visibility seam of
 * useNotebookState (card vncVrGk1hc9i, round two): the per-family draft
 * visibility (note vs gift), and the deliberate share / make-private actions
 * on a card. Pure move out of the former hooks.ts — no behaviour change.
 */
import { useState } from 'react';
import { api } from '../../../shell/bridge';
import { type EntryType, type NotebookItem } from '../types';
import type { NotebookFeed } from './feed';

export function useNotebookShare({ busyId, setBusyId, reload, partnerLabel, partnerLabelCap }: {
  busyId: NotebookFeed['busyId'];
  setBusyId: NotebookFeed['setBusyId'];
  reload: NotebookFeed['reload'];
  partnerLabel: string;
  partnerLabelCap: string;
}) {
  // Visibility of the next typed entry (card sYwPmYEIf4VC): 'us' is the
  // shared notebook, 'me' is private to this member.
  const [sharedDraft, setSharedDraft] = useState<'us' | 'me'>('us');

  /* Card ggTQe2KbB0A3 HIGH-3 — a GIFT capture keeps its own visibility, and
     it starts on 'me'. The shared draft is reset to 'us' after every save, so
     a gift typed straight after an ordinary note silently inherited 'us' and
     posted `shared: true` on the wire — exactly the widening the server's
     private default (card aDlJX4X7txMI) exists to prevent. One state per
     family, each reset to its own default: a surprise cannot ride in on the
     tail of an unrelated save. */
  const [giftDraft, setGiftDraft] = useState<'us' | 'me'>('me');

  /* The visibility the NEXT save will actually send — read once at the point
     of use so the radio, the hint and the request can never disagree. */
  const draftSharedFor = (t: EntryType): 'us' | 'me' =>
    t === 'gift' ? giftDraft : t === 'date' ? 'us' : sharedDraft;

  /** Card fyOZFYwAtbZy (PRD §8) — a just-me card becomes shared only as a
   *  deliberate action: confirm first, then the owner-only PUT flips it.
   *  The server enforces ownership (a partner's private row is 404, a shared
   *  row that isn't theirs is 403); this only shows the control to the owner
   *  of a private card, so nobody gets a button that can never work. */
  const shareEntry = async (item: NotebookItem) => {
    if (busyId) return;
    if (!window.confirm(`Share this with ${partnerLabel}? It will move into the shared notebook where both of you can see it.`)) return;
    setBusyId(item.itemId);
    try {
      await api(`/api/notebook/${item.itemId}`, { method: 'PUT', body: { shared: true } });
      await reload();
    } catch (e) {
      console.error('[Pear] notebook share failed', e);
      window.alert('Could not share that — try again.');
    } finally {
      setBusyId(null);
    }
  };

  /** Card fyOZFYwAtbZy (sol review 2026-10-16) — sharing has to be reversible
   *  or a mistaken share can only be undone by deleting the card. Owner-only,
   *  same PUT with shared:false. Offered on typed entries AND link cards now
   *  (card xNGOA5rZz9LV phase 3): a private link used to keep only a badge,
   *  with no way back to shared and no way in from shared. */
  const makePrivate = async (item: NotebookItem) => {
    if (busyId) return;
    if (!window.confirm(`Make this just-me again? ${partnerLabelCap} may already have seen it — from now on it stays out of their notebook.`)) return;
    setBusyId(item.itemId);
    try {
      await api(`/api/notebook/${item.itemId}`, { method: 'PUT', body: { shared: false } });
      await reload();
    } catch (e) {
      console.error('[Pear] notebook make-private failed', e);
      window.alert('Could not make that just-me — try again.');
    } finally {
      setBusyId(null);
    }
  };

  return {
    sharedDraft, setSharedDraft, giftDraft, setGiftDraft, draftSharedFor,
    shareEntry, makePrivate,
  };
}

export type NotebookShare = ReturnType<typeof useNotebookShare>;
